import UniversalScrollbar from 'universal-scrollbar'

const THUMB_COLOR = 'var(--color-border-primary)'
const TRACK_COLOR = 'var(--color-border-secondary)'

class LyleCsvTable extends HTMLElement {
  #abortController = null
  #sectionObserver = null
  #overflowObserver = null
  #scrollbar = null
  #trackSyncBound = null

  connectedCallback() {
    const url = this.dataset.url

    if (!url) {
      this.#finish()
      return
    }

    this.#abortController = new AbortController()

    const hiddenSection = this.closest('.is-hidden')
    // Load immediately when: no hidden ancestor, or the section is the :target
    // (CSS :target makes it visually visible even before JS removes is-hidden).
    if (hiddenSection && hiddenSection.id !== window.location.hash.slice(1)) {
      this.#deferUntilVisible(hiddenSection)
    } else {
      this.#loadTable(url)
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort()
    this.#abortController = null
    this.#sectionObserver?.disconnect()
    this.#sectionObserver = null
    this.#stopWatchOverflow()
    this.#destroyScrollbar()
  }

  refreshScrollbar() {
    this.#initScrollbar()
  }

  #deferUntilVisible(section) {
    this.#sectionObserver = new MutationObserver(() => {
      if (!section.isConnected) {
        this.#sectionObserver.disconnect()
        this.#sectionObserver = null
        return
      }
      if (!section.classList.contains('is-hidden')) {
        this.#sectionObserver.disconnect()
        this.#sectionObserver = null
        this.#loadTable(this.dataset.url)
      }
    })
    this.#sectionObserver.observe(section, {
      attributes: true,
      attributeFilter: ['class'],
    })
  }

  get #hasHeaderRow() {
    return this.hasAttribute('data-header-row')
  }

  get #hasHeaderColumn() {
    return this.hasAttribute('data-header-column')
  }

  async #loadTable(url) {
    try {
      const response = await fetch(url, {
        signal: this.#abortController?.signal,
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      const rows = this.#parseCsv(await response.text())

      if (rows.length > 0) {
        this.#renderTable(rows)
      }
    } catch (error) {
      if (error?.name === 'AbortError') return
      console.error('CSV table: unable to load data', error)
    } finally {
      this.#finish()
    }
  }

  #renderTable(rows) {
    const table = document.createElement('table')
    table.className = 'csv-table__table'

    const fragment = document.createDocumentFragment()

    rows.forEach((cells, rowIndex) => {
      const tr = document.createElement('tr')

      cells.forEach((value, cellIndex) => {
        const isHeaderColumn = cellIndex === 0 && this.#hasHeaderColumn
        const isHeaderRow = rowIndex === 0 && this.#hasHeaderRow
        const isHeader = isHeaderColumn || isHeaderRow

        const cell = document.createElement(isHeader ? 'th' : 'td')
        cell.textContent = value
        cell.classList.add(isHeaderRow ? 'text-body-md-bold' : 'text-body-md')

        if (isHeader) {
          cell.classList.add('highlight')
        }

        if (isHeaderRow) {
          cell.setAttribute('scope', 'col')
        } else if (isHeaderColumn) {
          cell.setAttribute('scope', 'row')
        }

        tr.appendChild(cell)
      })

      fragment.appendChild(tr)
    })

    table.appendChild(fragment)
    this.replaceChildren(table)
    this.#initScrollbar()
  }

  #initScrollbar() {
    this.#destroyScrollbar()

    if (!this.isConnected || this.scrollWidth <= this.clientWidth) {
      this.#watchOverflow()
      return
    }

    this.#stopWatchOverflow()

    this.#scrollbar = new UniversalScrollbar({
      target: this,
      width: 1,
      minThumbSize: 40,
      autoHide: false,
      smoothScroll: false,
      observeDOM: false,
      trackColor: TRACK_COLOR,
      thumbColor: THUMB_COLOR,
      thumbHoverColor: THUMB_COLOR,
      thumbActiveColor: THUMB_COLOR,
      zIndex: 2,
    })

    this.#trackSyncBound = () => {
      requestAnimationFrame(() => this.#syncInsetTrack())
    }
    this.addEventListener('scroll', this.#trackSyncBound, { passive: true })
    window.addEventListener('resize', this.#trackSyncBound)
    this.#trackSyncBound()
  }

  #watchOverflow() {
    if (this.#overflowObserver || !this.isConnected) return

    this.#overflowObserver = new ResizeObserver(() => {
      if (this.scrollWidth <= this.clientWidth) return
      this.#stopWatchOverflow()
      this.#initScrollbar()
    })
    this.#overflowObserver.observe(this)
  }

  #stopWatchOverflow() {
    this.#overflowObserver?.disconnect()
    this.#overflowObserver = null
  }

  #syncInsetTrack() {
    if (!this.closest('.csv-table-section__scroll')) return

    const wrapper = this.parentElement
    if (!wrapper?.classList.contains('us-wrapper')) return

    const track = wrapper.querySelector('.us-track.us-horizontal')
    const thumb = track?.querySelector('.us-thumb')
    if (!track || !thumb) return

    const trackWidth = track.clientWidth
    if (trackWidth <= 0) return

    const thumbWidth = Math.max(40, (this.clientWidth / this.scrollWidth) * trackWidth)
    const maxScroll = this.scrollWidth - this.clientWidth
    const maxOffset = Math.max(0, trackWidth - thumbWidth)
    const offset = maxScroll > 0 ? (this.scrollLeft / maxScroll) * maxOffset : 0

    thumb.style.width = `${thumbWidth}px`
    thumb.style.transform = `translateX(${offset}px)`
  }

  #destroyScrollbar() {
    if (this.#trackSyncBound) {
      this.removeEventListener('scroll', this.#trackSyncBound)
      window.removeEventListener('resize', this.#trackSyncBound)
      this.#trackSyncBound = null
    }

    if (!this.#scrollbar) return

    // destroy() removes shared #universal-scrollbar-styles used by every table.
    const sharedStyles = document.getElementById('universal-scrollbar-styles')
    this.#scrollbar.destroy()
    this.#scrollbar = null

    if (sharedStyles && !document.getElementById('universal-scrollbar-styles')) {
      document.head.appendChild(sharedStyles)
    }
  }

  #finish() {
    if (!this.isConnected) return
    this.removeAttribute('aria-busy')

    const placeholder = this.querySelector('.csv-table__placeholder')
    placeholder?.remove()
  }

  /**
   * Parse CSV text into an array of rows, each an array of cell strings.
   * Handles quoted fields, escaped quotes, embedded commas/newlines and CRLF,
   * then drops fully empty rows (mirrors the previous PapaParse behaviour).
   */
  #parseCsv(text) {
    const rows = []
    let row = []
    let field = ''
    let inQuotes = false
    let index = 0

    while (index < text.length) {
      const char = text[index]

      if (inQuotes) {
        if (char === '"') {
          if (text[index + 1] === '"') {
            field += '"'
            index += 2
            continue
          }

          inQuotes = false
          index += 1
          continue
        }

        field += char
        index += 1
        continue
      }

      if (char === '"') {
        inQuotes = true
        index += 1
        continue
      }

      if (char === ',') {
        row.push(field)
        field = ''
        index += 1
        continue
      }

      if (char === '\n' || char === '\r') {
        if (char === '\r' && text[index + 1] === '\n') {
          index += 1
        }

        row.push(field)
        rows.push(row)
        row = []
        field = ''
        index += 1
        continue
      }

      field += char
      index += 1
    }

    if (field !== '' || row.length > 0) {
      row.push(field)
      rows.push(row)
    }

    return rows.filter(cells => cells.some(cell => cell !== ''))
  }
}

if (!customElements.get('lyle-csv-table')) {
  customElements.define('lyle-csv-table', LyleCsvTable)
}
